import { FC, Fragment, useState } from "react"
import { useMutation, useQueryClient } from "react-query"

import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import DeleteIcon from "@mui/icons-material/DeleteOutline"
import EditIcon from "@mui/icons-material/DriveFileRenameOutline"

import { Button, Checkbox, InputText, Typography } from "@synapse-analytics/synapse-ui"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../../../../../API/VisionAPI"
import { definitions } from "../../../../../types/Generated/apiTypes"

import styles from "../CollectionsTab.module.scss"

type Branch = definitions["Branch"]
type Collection = definitions["Collection"]

/**
 * Props for the CollectionNameRow component.
 */
interface CollectionNameRowProps {
  /** The collection object to display and edit */
  collection: Collection
  /** Index of the collection in the list (used as fallback for key generation) */
  collectionIndex: number
  /** Whether the component is in edit mode (shows edit/delete buttons instead of checkbox) */
  isEditMode: boolean
  /** Set of selected branch IDs for computing selection status */
  selectedBranchesSet: Set<number>
  /** Callback invoked when the collection checkbox is toggled */
  onCheckboxChange: (collection: Collection, checked: boolean) => void
  /** Callback invoked when the delete button is clicked */
  onDelete: (collection: Collection) => void
}

/**
 * CollectionNameRow component displays and manages the collection name row.
 *
 * Features:
 * - Displays collection name with checkbox (normal mode) or edit/delete buttons (edit mode)
 * - Allows inline editing of collection name
 * - Computes and displays selection status (all/some branches selected)
 * - Manages its own editing state internally
 *
 * @param props - The component props
 * @returns The rendered collection name row component
 */
const CollectionNameRow: FC<CollectionNameRowProps> = ({
  collection,
  collectionIndex,
  isEditMode,
  selectedBranchesSet,
  onCheckboxChange,
  onDelete,
}) => {
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [editingName, setEditingName] = useState(collection.name || "")

  /**
   * Computes the selection status of branches within a collection.
   *
   * @param collectionBranches - Array of branches in the collection
   * @returns Object with `allSelected` and `someSelected` boolean flags
   */
  const getSelectionStatus = (collectionBranches: Branch[] = []) => {
    if (collectionBranches.length === 0) {
      return { allSelected: false, someSelected: false }
    }
    const selectedCount = collectionBranches.filter((branch) => selectedBranchesSet.has(branch.id!)).length
    return {
      allSelected: selectedCount === collectionBranches.length,
      someSelected: selectedCount > 0 && selectedCount < collectionBranches.length,
    }
  }

  const { allSelected, someSelected } = getSelectionStatus(collection.branches)

  /**
   * Mutation hook for updating collection name.
   * Invalidates collections query on success and shows toast notifications.
   */
  const { mutate: updateCollectionName, isLoading: isUpdatingName } = useMutation(
    async ({ collectionId, name }: { collectionId: number; name: string }) => {
      return VisionAPI.updateCollection(collectionId, { name })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("fetchCollections")
        NotificationUtils.toast("Collection name updated successfully", {
          severity: "success",
        })
        setIsEditing(false)
        // editingName will be synced by useEffect when collection.name updates after query refetch
      },
      onError: (error: any) => {
        NotificationUtils.toast(`Failed to update collection name: ${error?.message || "Unknown error"}`, {
          severity: "error",
        })
      },
    }
  )

  /**
   * Initiates editing mode for the collection name.
   * Sets editing state and initializes the editing name with current collection name.
   */
  const handleStartEdit = () => {
    setIsEditing(true)
    setEditingName(collection.name || "")
  }

  /**
   * Cancels editing mode and resets the editing name to the original collection name.
   */
  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingName(collection.name || "")
  }

  /**
   * Confirms the name edit and triggers the update mutation.
   * Only proceeds if the trimmed name is non-empty and collection has an ID.
   */
  const handleConfirmEdit = () => {
    const newName = editingName.trim()
    if (newName && collection.id) {
      updateCollectionName({ collectionId: collection.id, name: newName })
    }
  }

  if (isEditing) {
    return (
      <div className={styles.collectionNameContainer}>
        <InputText
          value={editingName}
          handleChange={(e) => setEditingName(e.target.value as string)}
          hideDescription
          autofocus
          width={100}
        />
        <Button
          size="small"
          onClick={handleCancelEdit}
          startIcon={<CloseIcon className={`${styles.buttonIcon} ${styles.negativeIcon}`} />}
        />
        <Button
          size="small"
          onClick={handleConfirmEdit}
          startIcon={<CheckIcon className={`${styles.buttonIcon} ${styles.checkIcon}`} />}
          isLoading={isUpdatingName}
          disabled={isUpdatingName}
        />
      </div>
    )
  }

  return (
    <div className={styles.collectionNameContainer}>
      {isEditMode ? (
        <Fragment>
          <Typography variant="a">{collection.name}</Typography>
          <Button
            variant="secondary"
            size="small"
            onClick={handleStartEdit}
            startIcon={<EditIcon className={styles.buttonIcon} />}
          />
          <Button
            variant="secondary"
            size="small"
            onClick={() => onDelete(collection)}
            startIcon={<DeleteIcon className={styles.buttonIcon} />}
          />
        </Fragment>
      ) : (
        <Checkbox
          label={collection.name}
          checked={allSelected || someSelected}
          indeterminate={someSelected}
          name={`collection-${collection.id || collectionIndex}`}
          onChange={(e) => onCheckboxChange(collection, (e.target as HTMLInputElement).checked)}
        />
      )}
    </div>
  )
}

export default CollectionNameRow
